import {
    Button,
    ButtonSizes,
    ButtonVariants,
    IconNames,
} from '@triumph/design-system';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    ButtonGroup,
    ContentWrapper,
    DocumentInfo,
    DocumentPreviewImg,
    DocumentPreviewWrapper,
    FileNameDiv,
    Header,
    LeftWrapper,
    RightWrapper,
    TopWrapper,
    UploadDiv,
} from './DocumentPreview.StyledComponents';
import { DocumentPreviewProps } from './DocumentPreview.types';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import ViewDocumentModal from '@/app/shared/components/ViewDocumentModal';
import { DetailType, DocType } from '../DocumentsCard/DocumentsCard.types';
import DetailItem from '../DetailItem';

const deltaDocTypeMapper: any = (
    docType: string,
    detailType: DetailType
) => {
    switch (docType) {
        case 'Invoice':
            return DocType.Invoice;
        case 'RateConfirmation':
        case 'Rate Confirm':
            return DocType.RateConfirmation;
        case 'BillOfLading':
        case 'BOL':
            return DocType.BillOfLading;
        case 'Lumper':
            return DocType.Lumper;
        case 'PalletReceipt':
        case 'Pallet Receipt':
        case 'Gate Ticket':
        case 'Scale Ticket':
            return DocType.PalletReceipt;
        case 'CarrierDoc':
        case 'Carrier Doc':
            return DocType.CarrierDoc;
        case 'Other':
            return DocType.Other;
        default:
            return detailType === DetailType.Advance &&
                docType &&
                docType.length
                ? 'Fuel Advance Document'
                : docType && docType.length
                ? 'Invoice Document'
                : null;
    }
};

const formatDateTime = (date?: string) => {
    const options: any = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };
    return (date && new Date(date).toLocaleString(undefined, options))?.concat(
        ' CT'
    );
};

const getTextOrDash = (text?: string) => {
    if (text == null || text === '') {
        return '\u2014';
    }
    return text;
};

const getDefaultSelection = (docType: DocType, detailType: DetailType) => {
    return deltaDocTypeMapper(docType, detailType);
};

const DocumentPreview: FC<DocumentPreviewProps> = ({
    documentPreviewTestId,
    filename,
    documentUrl,
    documentUploadDate,
    docType,
    detailType,
}) => {
    const { breakpoint } = useAppContext();

    const { t } = useTranslation('invoicesDashboard');

    const [isViewDocumentModalVisible, setIsViewDocumentModalVisible] =
        useState<boolean>(false);

    const handleCloseModal = () => setIsViewDocumentModalVisible(false);

    const handleThumbnailClick = () => setIsViewDocumentModalVisible(true);

    const handleThumbnailKeydown = (e: { code: string }) => {
        if (['Enter', 'Space'].includes(e.code)) {
            setIsViewDocumentModalVisible(true);
        }
    };

    const handleInformationButtonClick = () =>
        setIsViewDocumentModalVisible(true);

    const handleDownloadButtonClick = async () => {
        const anchor = document.createElement('a');
        anchor.href = documentUrl;
        anchor.target = '_blank';
        anchor.download = filename;
        anchor.click();
    };

    const modalHeader = (
        <Header>
            <div>{t('invoiceDocuments.modalViewDocHeader')}</div>
            <DocumentInfo data-testid="document-info" breakpoint={breakpoint}>
                <ContentWrapper
                    data-testid="content-wrapper"
                    breakpoint={breakpoint}
                >
                    <FileNameDiv data-testid="filename-div">
                        <DetailItem
                            context="Filename"
                            value={getTextOrDash(filename)}
                        ></DetailItem>
                    </FileNameDiv>
                    <UploadDiv data-testid="upload-div">
                        <DetailItem
                            context="Uploaded"
                            value={getTextOrDash(
                                formatDateTime(documentUploadDate)
                            )}
                        ></DetailItem>
                    </UploadDiv>
                </ContentWrapper>
            </DocumentInfo>
        </Header>
    );

    return (
        <>
            <DocumentPreviewWrapper
                key={'documentId'}
                breakpoint={breakpoint}
                data-testid={documentPreviewTestId}
            >
                <TopWrapper breakpoint={breakpoint}>
                    <LeftWrapper>
                        <DocumentPreviewImg
                            tabIndex={0}
                            aria-label="View Document"
                            data-testid="file-preview-img"
                            onClick={handleThumbnailClick}
                            onKeyDown={handleThumbnailKeydown}
                            src={documentUrl}
                        />
                    </LeftWrapper>
                    <RightWrapper breakpoint={breakpoint}>
                        <DetailItem
                            context="Doc Type"
                            value={getTextOrDash(
                                getDefaultSelection(docType, detailType)
                            )}
                        ></DetailItem>
                        <DetailItem
                            context="Uploaded"
                            value={getTextOrDash(
                                formatDateTime(documentUploadDate)
                            )}
                        ></DetailItem>
                        <ButtonGroup data-testid="button-group">
                            <div data-testid="information-div">
                                <Button
                                    variant={ButtonVariants.tertiary}
                                    isDanger={false}
                                    onClick={handleInformationButtonClick}
                                    aria-label={t('documentInformationAria', {
                                        filename,
                                    })}
                                    size={ButtonSizes.small}
                                    icon={IconNames.Alerts_Information}
                                ></Button>
                            </div>
                            <div data-testid="download-div">
                                <Button
                                    variant={ButtonVariants.tertiary}
                                    isDanger={false}
                                    onClick={handleDownloadButtonClick}
                                    aria-label={t('downloadDocumentAria', {
                                        filename,
                                    })}
                                    size={ButtonSizes.small}
                                    icon={IconNames.Interface_Download}
                                ></Button>
                            </div>
                        </ButtonGroup>
                    </RightWrapper>
                </TopWrapper>
            </DocumentPreviewWrapper>
            <ViewDocumentModal
                handleClose={handleCloseModal}
                isViewModalOpen={isViewDocumentModalVisible}
                filename={filename}
                url={documentUrl}
                customHeaderContent={modalHeader}
                isPdfPreview={false}
            />
        </>
    );
};

export default DocumentPreview;
